import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { EventModel, EventStatus, LocationModel } from '../types/Event';
import { IdeaModel } from '../types/Idea';

/**
 * Get a single event by ID
 */
export const getEventById = async (eventId: string): Promise<EventModel | null> => {
  try {
    const eventRef = doc(db, 'events', eventId);
    const eventSnap = await getDoc(eventRef);
    
    if (eventSnap.exists()) {
      const data = eventSnap.data();
      
      // Convert location data if it exists
      let location: LocationModel | undefined;
      if (data.location) {
        location = {
          name: data.location.name || '',
          address: data.location.address || '',
          latitude: data.location.latitude || 0,
          longitude: data.location.longitude || 0,
          city: data.location.city,
          country: data.location.country,
          postalCode: data.location.postalCode,
          state: data.location.state,
          street: data.location.street,
          streetNumber: data.location.streetNumber,
          subLocality: data.location.subLocality,
          subThoroughfare: data.location.subThoroughfare,
          thoroughfare: data.location.thoroughfare,
        };
      }

      // Convert Firestore data to our EventModel format
      const event: EventModel = {
        id: eventSnap.id,
        name: data.name || '',
        creatorUid: data.creatorUid || '',
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        eventDate: data.eventDate?.toDate?.()?.toISOString() || data.eventDate,
        invitedUids: data.invitedUids || [],
        votedIdeaIds: data.votedIdeaIds || [],
        userVotes: data.userVotes || {},
        description: data.description,
        votingDeadline: data.votingDeadline?.toDate?.()?.toISOString(),
        adminUids: data.adminUids || [],
        eventResponses: data.eventResponses || {},
        status: data.status as EventStatus || EventStatus.active,
        backgroundIndex: data.backgroundIndex || 0,
        imageName: data.imageName,
        location: location,
        isOpenEvent: data.isOpenEvent,
      };
      
      return event;
    }
    
    return null;
  } catch (error) {
    throw new Error('Failed to fetch event');
  }
};

/**
 * Get all public events (for demo purposes)
 */
export const getPublicEvents = async (): Promise<EventModel[]> => {
  try {
    const eventsRef = collection(db, 'events');
    const q = query(eventsRef, where('status', '==', 'active'));
    const querySnapshot = await getDocs(q);
    
    const events: EventModel[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Convert location data if it exists
      let location: LocationModel | undefined;
      if (data.location) {
        location = {
          name: data.location.name || '',
          address: data.location.address || '',
          latitude: data.location.latitude || 0,
          longitude: data.location.longitude || 0,
          city: data.location.city,
          country: data.location.country,
          postalCode: data.location.postalCode,
          state: data.location.state,
          street: data.location.street,
          streetNumber: data.location.streetNumber,
          subLocality: data.location.subLocality,
          subThoroughfare: data.location.subThoroughfare,
          thoroughfare: data.location.thoroughfare,
        };
      }
      
      const event: EventModel = {
        id: doc.id,
        name: data.name || '',
        creatorUid: data.creatorUid || '',
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        eventDate: data.eventDate?.toDate?.()?.toISOString() || data.eventDate,
        invitedUids: data.invitedUids || [],
        votedIdeaIds: data.votedIdeaIds || [],
        userVotes: data.userVotes || {},
        description: data.description,
        votingDeadline: data.votingDeadline?.toDate?.()?.toISOString(),
        adminUids: data.adminUids || [],
        eventResponses: data.eventResponses || {},
        status: data.status as EventStatus || EventStatus.active,
        backgroundIndex: data.backgroundIndex || 0,
        imageName: data.imageName,
        location: location,
        isOpenEvent: data.isOpenEvent,
      };
      events.push(event);
    });
    
    return events;
  } catch (error) {
    throw new Error('Failed to fetch events');
  }
};

/**
 * Get ideas for an event
 */
export const getIdeasForEvent = async (eventId: string): Promise<IdeaModel[]> => {
  try {
    // Ideas are stored as a subcollection under each event: events/{eventId}/ideas
    const ideasRef = collection(db, 'events', eventId, 'ideas');
    const querySnapshot = await getDocs(ideasRef);
    
    const ideas: IdeaModel[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      const idea: IdeaModel = {
        id: doc.id,
        eventId: data.eventId || eventId, // Use the eventId parameter if not in data
        proposerUid: data.proposerUid || '',
        description: data.description || '',
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        votes: data.votes || 0,
        upvotes: data.upvotes || 0,
        downvotes: data.downvotes || 0,
        ideaType: data.ideaType || 'idea',
        assignedUids: data.assignedUids || [],
        isPicked: data.isPicked || false,
        isDone: data.isDone || false,
        proposedDate: data.proposedDate?.toDate?.()?.toISOString(),
      };
      ideas.push(idea);
    });
    
    // If no ideas found and it's a demo event, return demo data
    if (ideas.length === 0 && eventId === 'demo-event-id') {
      return getDemoIdeas(eventId);
    }
    
    return ideas;
  } catch (error) {
    // If Firebase fails and it's a demo event, return demo data
    if (eventId === 'demo-event-id') {
      return getDemoIdeas(eventId);
    }
    
    throw new Error('Failed to fetch ideas');
  }
};

/**
 * Test Firebase connection and list all events
 */
export const testFirebaseConnection = async (): Promise<void> => {
  try {
    const eventsRef = collection(db, 'events');
    const querySnapshot = await getDocs(eventsRef);
    
    // Connection successful - no logging needed
  } catch (error) {
    // Connection failed - error will be thrown
    throw new Error('Firebase connection failed');
  }
};

/**
 * Submit RSVP response (read-only for web app)
 * Note: This is a placeholder - in a real implementation, you'd need
 * a backend API or Firebase Functions to handle RSVP submissions
 */
export const submitRSVP = async (eventId: string, email: string, response: string): Promise<void> => {
  // RSVP submission not implemented for web app
  // You could implement this later with:
  // 1. Firebase Functions
  // 2. Backend API
  // 3. Firestore rules that allow limited writes
  
  throw new Error('RSVP submission not implemented for web app');
};

/**
 * Get demo ideas for demo events (when no real data exists)
 */
export const getDemoIdeas = (eventId: string): IdeaModel[] => {
  if (eventId !== 'demo-event-id') {
    return [];
  }

  return [
    // Ideas
    {
      id: 'demo-idea-1',
      eventId: 'demo-event-id',
      proposerUid: 'demo-user-1',
      description: 'Let\'s have a pizza making contest! Everyone brings their favorite toppings and we vote on the best creation.',
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
      votes: 8,
      upvotes: 12,
      downvotes: 4,
      ideaType: 'idea',
      assignedUids: [],
      isPicked: true,
      isDone: false,
    },
    {
      id: 'demo-idea-2',
      eventId: 'demo-event-id',
      proposerUid: 'demo-user-2',
      description: 'Movie night with popcorn and cozy blankets! We could watch a comedy or action movie.',
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
      votes: 5,
      upvotes: 8,
      downvotes: 3,
      ideaType: 'idea',
      assignedUids: [],
      isPicked: false,
      isDone: false,
    },
    {
      id: 'demo-idea-3',
      eventId: 'demo-event-id',
      proposerUid: 'demo-user-3',
      description: 'Board game tournament! We could play Catan, Ticket to Ride, or other fun games.',
      createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
      votes: 3,
      upvotes: 6,
      downvotes: 3,
      ideaType: 'idea',
      assignedUids: [],
      isPicked: false,
      isDone: false,
    },
    // Tasks
    {
      id: 'demo-task-1',
      eventId: 'demo-event-id',
      proposerUid: 'demo-user-1',
      description: 'Buy pizza ingredients and toppings',
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      votes: 0,
      upvotes: 0,
      downvotes: 0,
      ideaType: 'task',
      assignedUids: ['demo-user-1', 'demo-user-2'],
      isPicked: false,
      isDone: false,
    },
    {
      id: 'demo-task-2',
      eventId: 'demo-event-id',
      proposerUid: 'demo-user-2',
      description: 'Set up the dining area and tables',
      createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
      votes: 0,
      upvotes: 0,
      downvotes: 0,
      ideaType: 'task',
      assignedUids: ['demo-user-3'],
      isPicked: false,
      isDone: true,
    },
    {
      id: 'demo-task-3',
      eventId: 'demo-event-id',
      proposerUid: 'demo-user-3',
      description: 'Prepare drinks and snacks',
      createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      votes: 0,
      upvotes: 0,
      downvotes: 0,
      ideaType: 'task',
      assignedUids: ['demo-user-1'],
      isPicked: false,
      isDone: false,
    },
    // Alternative Dates
    {
      id: 'demo-date-1',
      eventId: 'demo-event-id',
      proposerUid: 'demo-user-1',
      description: 'Saturday evening works better for me',
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      votes: 4,
      upvotes: 6,
      downvotes: 2,
      ideaType: 'alternative_date',
      assignedUids: [],
      isPicked: false,
      isDone: false,
      proposedDate: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString(), // 8 days from now
    },
    {
      id: 'demo-date-2',
      eventId: 'demo-event-id',
      proposerUid: 'demo-user-2',
      description: 'Sunday afternoon would be perfect',
      createdAt: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
      votes: 2,
      upvotes: 4,
      downvotes: 2,
      ideaType: 'alternative_date',
      assignedUids: [],
      isPicked: true,
      isDone: false,
      proposedDate: new Date(Date.now() + 9 * 24 * 60 * 60 * 1000).toISOString(), // 9 days from now
    },
  ];
};
